import { getCombinations } from "store/product/product.saga";
import {
  ColorImage,
  Combination,
  Product,
  ProductResponse,
  Variant,
} from "../types/products";

export type ProductValidationErrors = {
  name?: string;
  description?: string;
  category_id?: string;
  brand_id?: string;
  thumbnail_img?: string;
  photos?: string;
  price?: string;
  currency_id?: string;
  current_stock?: string;
  weight?: string;
  weight_unit?: string;
  width?: string;
  length?: string;
  height?: string;
};

export const isProductFormValidated = (product: Product) => {
  const errors = {
    name: product.name.length === 0,
    description: product.description.length < 50,
    category_id: product.category_id === 0,
    brand_id: product.category_id === 0,
    thumbnail_img: product.thumbnail_img === null,
    photos: product.photos.length === 0,
    price: product.price === 0,
    currency_id: product.currency_id === 0,
    current_stock: product.current_stock === 0,
    weight: product.weight === 0,
    weight_unit: product.weight_unit.length === 0,
    width: product.width === 0,
    length: product.length === 0,
    height: product.height === 0,
  };
  return Object.values(errors).every((error) => !error);
};

export const validateProductForm = (
  product: Product
): ProductValidationErrors => {
  const errors: ProductValidationErrors = {};

  if (product.name.length === 0) {
    errors.name = "Please name is required";
  }
  if (product.description.length < 50) {
    errors.description =
      "Please description is required and must be at least 50 characters";
  }
  if (product.category_id === 0) {
    errors.category_id = "Please category is required";
  }
  if (product.brand_id === 0) {
    errors.brand_id = "Please brand is required";
  }
  if (product.thumbnail_img === null) {
    errors.thumbnail_img = "Please thumbnail is required";
  }
  if (product.photos.length === 0) {
    errors.photos = "Please photos are required";
  }
  if (product.price === 0) {
    errors.price = "Please price is required";
  }
  if (product.currency_id === 0) {
    errors.currency_id = "Please currency is required";
  }
  if (product.current_stock === 0) {
    errors.current_stock = "Please quantity is required";
  }
  if (product.weight === 0) {
    errors.weight = "Please weight is required";
  }
  if (product.weight_unit.length === 0) {
    errors.weight_unit = "Please weight unit is required";
  }
  if (product.width === 0) {
    errors.width = "Please width is required";
  }
  if (product.length === 0) {
    errors.length = "Please length is required";
  }
  if (product.height === 0) {
    errors.height = "Please height is required";
  }
  return errors;
};

export function allCombinationsValid(combinations: Combination[]): boolean {
  return combinations.every(
    (combination) => combination.stock > 0 && combination.price > 0
  );
}
export const validateColorImages = (colorImages: ColorImage[]): boolean => {
  if (!Array.isArray(colorImages)) {
    throw new Error("Invalid input: colorImages should be an array.");
  }

  return colorImages.every(
    (colorImage) =>
      Array.isArray(colorImage.images) && colorImage.images.length > 0
  );
};

export function getVariantCombinations(variants: Variant[]): Combination[] {
  return variants.map((variant) => {
    const colorPart = variant.color ? variant.color.name : null;

    // Fix: Use correct keys from ResponseAttribute
    const attributes =
      variant.attributes.length > 0
        ? variant.attributes.map((attr) => ({
            attribute_id: attr.attribute_id,
            attribute_value_id: attr.id,
          }))
        : [];
    const _attributes =
      variant.attributes.length > 0
        ? variant.attributes.map((attr) => ({
            attribute_id: attr.attribute_id,
            value: attr.value,
          }))
        : [];

    const attributesName =
      _attributes.length > 0
        ? _attributes.map((a) => a.value).join(" / ")
        : null;

    const combinationName = [colorPart, attributesName]
      .filter(Boolean)
      .join(" - ");

    return {
      variant_id: variant.id,
      color_id: variant.color_id ?? null,
      color_name: colorPart,
      attribute_combination:
        attributes.length > 0
          ? {
              combination_name: attributesName ?? "",
              attributes: attributes,
            }
          : null,
      combination_name: combinationName,
      stock: variant.stock,
      price: variant.price,
    };
  });
}

export const convertProductResponseToProduct = (
  productResponse: ProductResponse
): Product => {
  return {
    id: Number(productResponse.id),
    name: productResponse.name,
    slug: productResponse.slug,
    added_by: productResponse.added_by as
      | "customer"
      | "admin"
      | "staff"
      | "vendor",
    user_id: Number(productResponse.user_id),
    category_id: Number(productResponse.category_id),
    category_tags: [], // Assuming category_tags are not in Root
    brand_id: Number(productResponse.brand_id),
    thumbnail_img: productResponse.thumbnail_img,
    photos: productResponse.photos,
    color_images: productResponse.color_images.map((image) => ({
      ...image,
      color_id: Number(image.color_id),
    })),
    description: productResponse.description,
    specifications:
      productResponse.specifications != null &&
      productResponse.specifications.length
        ? productResponse.specifications
        : null,
    currency_id: Number(productResponse.currency_id),
    price: Number(productResponse.price),
    weight: productResponse.weight ? Number(productResponse.weight) : 0,
    weight_unit: productResponse.weight_unit,
    width: productResponse.width ? Number(productResponse.width) : 0,
    length: productResponse.length ? Number(productResponse.length) : 0,
    height: productResponse.height ? Number(productResponse.height) : 0,
    tags: productResponse.tags,
    current_stock: Number(productResponse.current_stock),
    min_qty: Number(productResponse.min_qty),
    low_stock_quantity: productResponse.low_stock_quantity
      ? Number(productResponse.low_stock_quantity)
      : null,
    discount: productResponse.discount
      ? Number(productResponse.discount)
      : null,
    discount_type: productResponse.discount_type as "amount" | "percent" | null,
    discount_start_date: productResponse.discount_start_date || null,
    discount_end_date: productResponse.discount_end_date || null,
    meta_title: productResponse.meta_title || null,
    meta_description: productResponse.meta_description || null,
    meta_img: productResponse.meta_img || null,
    barcode: productResponse.barcode || null,
    refundable: Number(productResponse.refundable),
    todays_deal: Number(productResponse.todays_deal),
    published: productResponse.published ? 1 : 0,
    approved: Number(productResponse.approved),
    cash_on_delivery: Number(productResponse.cash_on_delivery),
    featured: Number(productResponse.featured),
    seller_featured: Number(productResponse.seller_featured),
    colors: productResponse.variants.map((variant) => Number(variant.color_id)),
    attributes: productResponse.variants.flatMap((variant) =>
      variant.attributes.map((attr) => Number(attr.attribute_id))
    ),
    variations: getVariantCombinations(productResponse.variants),
  };
};
